import type { Constraint, Entity } from '@lakaran/geometry';
import type { CadDocument } from './document';

export type EntityOp =
  | { kind: 'add'; entity: Entity }
  | { kind: 'remove'; entity: Entity }
  | { kind: 'update'; before: Entity; after: Entity };

export type ConstraintOp =
  | { kind: 'constraint-add'; constraint: Constraint }
  | { kind: 'constraint-remove'; constraint: Constraint };

/** Anything the history can apply to the document. */
export type DocumentOp = EntityOp | ConstraintOp;

export class Transaction {
  readonly ops: DocumentOp[] = [];

  add(entity: Entity): this {
    this.ops.push({ kind: 'add', entity });
    return this;
  }

  remove(entity: Entity): this {
    this.ops.push({ kind: 'remove', entity });
    return this;
  }

  update(before: Entity, after: Entity): this {
    this.ops.push({ kind: 'update', before, after });
    return this;
  }

  addConstraint(constraint: Constraint): this {
    this.ops.push({ kind: 'constraint-add', constraint });
    return this;
  }

  removeConstraint(constraint: Constraint): this {
    this.ops.push({ kind: 'constraint-remove', constraint });
    return this;
  }

  get isEmpty(): boolean {
    return this.ops.length === 0;
  }
}

interface HistoryEntry {
  label: string;
  ops: DocumentOp[];
}

/** Undo/redo over entity add/remove/update operations. */
export class History {
  private readonly undoStack: HistoryEntry[] = [];
  private readonly redoStack: HistoryEntry[] = [];
  private listeners: Array<() => void> = [];

  constructor(private readonly doc: CadDocument) {}

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoLabel(): string | null {
    return this.undoStack[this.undoStack.length - 1]?.label ?? null;
  }

  onChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  commit(tx: Transaction, label: string): void {
    if (tx.isEmpty) return;
    this.apply(tx.ops);
    this.undoStack.push({ label, ops: [...tx.ops] });
    this.redoStack.length = 0;
    this.notify();
  }

  /**
   * Applies operations and folds them into the previous entry, so a follow-up pass
   * (for example the constraint solver after a move) still undoes as one step.
   */
  extendLast(tx: Transaction): void {
    if (tx.isEmpty) return;
    const entry = this.undoStack[this.undoStack.length - 1];
    if (!entry) {
      this.commit(tx, 'SOLVE');
      return;
    }
    this.apply(tx.ops);
    entry.ops.push(...tx.ops);
    this.redoStack.length = 0;
    this.notify();
  }

  undo(): string | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    this.applyInverse(entry.ops);
    this.redoStack.push(entry);
    this.notify();
    return entry.label;
  }

  redo(): string | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    this.apply(entry.ops);
    this.undoStack.push(entry);
    this.notify();
    return entry.label;
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.notify();
  }

  private apply(ops: readonly DocumentOp[]): void {
    for (const op of ops) {
      if (op.kind === 'add') this.doc.add(op.entity);
      else if (op.kind === 'remove') this.doc.remove(op.entity.id);
      else if (op.kind === 'update') this.doc.update(op.after);
      else if (op.kind === 'constraint-add') this.doc.addConstraint(op.constraint);
      else this.doc.removeConstraint(op.constraint.id);
    }
  }

  private applyInverse(ops: readonly DocumentOp[]): void {
    for (let i = ops.length - 1; i >= 0; i--) {
      const op = ops[i]!;
      if (op.kind === 'add') this.doc.remove(op.entity.id);
      else if (op.kind === 'remove') this.doc.add(op.entity);
      else if (op.kind === 'update') this.doc.update(op.before);
      else if (op.kind === 'constraint-add') this.doc.removeConstraint(op.constraint.id);
      else this.doc.addConstraint(op.constraint);
    }
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}
